import { FormsEntities, Mobile } from '@bimser/common';
import {
	BCDateRangePicker,
	BCDateRangePickerRef,
	BCMobileRangePicker,
	BCNotification,
	RangePickerValue,
	findMessage
} from '@bimser/components';
import { FormItemTypes, IDictionary, createGuid, isNullOrUndefined, mobile } from '@bimser/core';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import * as React from 'react';
import { Portal } from 'react-portal';
import {
	ActiveDateTypes,
	DateLimitTypes,
	DatePickerMode,
	DisabledDateTypes,
	IPropertyField,
	SizeType
} from '../../../../entities/common';
import { IUserEvent } from '../../../../entities/services';
import { geti18nValue } from '../../../../helper';
import EditControl from '../../base/EditControl';
import {
	IControlExecutedServerEvent,
	IPropertyChangedEventArgs,
	IPropertyChangingEventArgs
} from '../../entities/EventArgs';
import { filterFields } from '../../helper';
import Styles from './assets/style.scss';
import IDateRangeItemProperties from './entities/IDateRangeItemProperties';
import IDateRangePickerProps from './entities/IDateRangePickerProps';
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

import classNames from 'classnames/bind';
import _isEmpty from 'lodash/isEmpty';
const cx = classNames.bind(Styles);

const pickerPopupId = 'dateRangePickerPopupContainer';

export default class DateRangePicker extends EditControl<[string, string]> {
	startDate: IDateRangeItemProperties;
	endDate: IDateRangeItemProperties;
	format: string;
	showTime: boolean;
	showToday: boolean;
	mode?: DatePickerMode;
	disabledDateType?: DisabledDateTypes;
	activeDateType: ActiveDateTypes;
	dateLimitType: DateLimitTypes;
	disabledDates: Array<string>;
	disabledDateRange?: number;
	sizeType: SizeType;
	allowClear: boolean;
	__startDateTodaySetFlag: boolean;
	__endDateTodaySetFlag: boolean;
	inputRef = React.createRef<BCDateRangePickerRef>();
	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.DateRangePicker;

		this.showTime = false;
		this.showToday = false;
		this.mode = null;
		this.disabledDateType = null;
		this.activeDateType = null;
		this.dateLimitType = 'active';
		this.disabledDates = null;
		this.disabledDateRange = 0;
		this.allowClear = true;
		this.startDate = {
			allowEmpty: true,
			setTodayAsDefault: false,
			disabledHours: '',
			disabledMinutes: '',
			disabledSeconds: '',
			placeholder: {},
			value: ''
		};
		this.endDate = {
			allowEmpty: true,
			setTodayAsDefault: false,
			disabledHours: '',
			disabledMinutes: '',
			disabledSeconds: '',
			placeholder: {},
			value: ''
		};

		this.triggerValueChanged = this.triggerValueChanged.bind(this);
		this.disabledTime = this.disabledTime.bind(this);
		this.disabledDate = this.disabledDate.bind(this);
		this.__isSelectedTimeDisabled = this.__isSelectedTimeDisabled.bind(this);
		this.__getComponentByPlatform = this.__getComponentByPlatform.bind(this);

		this.__excludeChangingProperties = ['value', 'text'];

		const valueChangingFn = (eventArgs: FormsEntities.IPropertyChangeEventArgs) =>
			new Promise<void>((resolve, reject) => {
				const startDateChanging = eventArgs.dataField.join('.') === 'startDate.value';
				const endDateChanging = eventArgs.dataField.join('.') === 'endDate.value';

				if (startDateChanging || endDateChanging) {
					let hasValueChange = false;
					let newValue: [string, string] = [null, null];

					if (startDateChanging && eventArgs.value !== this.startDate.value) {
						hasValueChange = true;
						newValue = [eventArgs.value, this.endDate.value];
					} else if (endDateChanging && eventArgs.value !== this.endDate.value) {
						hasValueChange = true;
						newValue = [this.startDate.value, eventArgs.value];
					}

					if (hasValueChange) {
						const valueArgument: IPropertyChangingEventArgs<[string, string]> = {
							name: this.name,
							newValue,
							oldValue: [this.startDate.value, this.endDate.value],
							cancel: false
						};

						// TRIGGER VALUE CHANGING CLIENT EVENT
						this.emit<IPropertyChangingEventArgs<[string, string]>>(
							'event:OnValueChanging',
							valueArgument
						)
							.then(() => {
								if (!valueArgument.cancel) {
									// TRIGGER VALUE CHANGING SERVER EVENT
									this.emit<
										IUserEvent<IPropertyChangedEventArgs<[string, string]>>
									>('event:server:OnValueChanging', {
										argument: valueArgument,
										controlId: this.name,
										name: 'ValueChanging',
										sender: 'web'
									})
										.then(args => {
											const executedServerEvent: IControlExecutedServerEvent =
												args[0]?.executedServerEvent;
											if (
												!executedServerEvent ||
												!executedServerEvent?.parameters.cancel
											) {
												resolve();
											} else {
												reject();
											}
										})
										.catch(() => {
											reject();
										});
								} else {
									reject();
								}
							})
							.catch(() => {
								reject();
							});
					} else {
						reject();
					}
				} else {
					resolve();
				}
			});

		this.subscribe('system:PropertyChanging:startDate', valueChangingFn);
		this.subscribe('system:PropertyChanging:endDate', valueChangingFn);
		this.subscribe('trigger:OnValueChanged', (value: string) => {
			// [string,string]
			const dates = JSON.parse(value);
			if (_isEmpty(dates[0]) && _isEmpty(dates[1])) {
				this.triggerValueChanged(undefined, [null, null]);
			} else {
				this.triggerValueChanged(undefined, dates);
			}
		});
	}

	__loadProperties(data: IDateRangePickerProps) {
		super.__loadProperties(data);

		this.__proxyChanged.format = data.format;
		this.__proxyChanged.showTime = data.showTime;
		this.__proxyChanged.showToday = data.showToday;

		if (data.startDate) {
			this.__proxyChanged.startDate.value = data.startDate.value;
			this.__proxyChanged.startDate.setTodayAsDefault = data.startDate.setTodayAsDefault;
			this.__proxyChanged.startDate.allowEmpty = data.startDate.allowEmpty;
			this.__proxyChanged.startDate.disabledHours = data.startDate.disabledHours;
			this.__proxyChanged.startDate.disabledMinutes = data.startDate.disabledMinutes;
			this.__proxyChanged.startDate.disabledSeconds = data.startDate.disabledSeconds;
			this.__proxyChanged.startDate.placeholder = data.startDate.placeholder;
		}

		if (data.endDate) {
			this.__proxyChanged.endDate.value = data.endDate.value;
			this.__proxyChanged.endDate.setTodayAsDefault = data.endDate.setTodayAsDefault;
			this.__proxyChanged.endDate.allowEmpty = data.endDate.allowEmpty;
			this.__proxyChanged.endDate.disabledHours = data.endDate.disabledHours;
			this.__proxyChanged.endDate.disabledMinutes = data.endDate.disabledMinutes;
			this.__proxyChanged.endDate.disabledSeconds = data.endDate.disabledSeconds;
			this.__proxyChanged.endDate.placeholder = data.endDate.placeholder;
		}

		this.__proxyChanged.mode = data.mode;
		this.__proxyChanged.disabledDateRange = data.disabledDateRange;
		this.__proxyChanged.disabledDates = data.disabledDates;
		this.__proxyChanged.disabledDateType = data.disabledDateType;
		this.__proxyChanged.activeDateType = data.activeDateType;
		this.__proxyChanged.dateLimitType = data.dateLimitType;
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.allowClear = data.allowClear;

		if (
			this.startDate?.setTodayAsDefault &&
			!this.startDate?.value &&
			!this.__startDateTodaySetFlag
		) {
			this.__proxyChanging.startDate.value = dayjs(
				dayjs(),
				this.format || dayjs().localeData().longDateFormat('L')
			).format();
			this.__startDateTodaySetFlag = true;
		}
		if (
			this.endDate?.setTodayAsDefault &&
			!this.endDate?.value &&
			!this.__endDateTodaySetFlag
		) {
			this.__proxyChanging.endDate.value = dayjs(
				dayjs(),
				this.format || dayjs().localeData().longDateFormat('L')
			).format();
			this.__endDateTodaySetFlag = true;
		}
	}

	__beforeLoadValueProperty(data: IDateRangePickerProps) {
		const startDateChanged = this.startDate?.value !== data.startDate?.value;
		const endDateChanged = this.endDate?.value !== data.endDate?.value;

		if (startDateChanged || endDateChanged) {
			const newDates: [string, string] =
				isNullOrUndefined(data.startDate?.value) || isNullOrUndefined(data.endDate?.value)
					? [this.startDate?.value, this.endDate?.value]
					: [data.startDate?.value, data.endDate?.value];
			this.__loadValueProperty(newDates);
		}
	}

	__isSelectedTimeDisabled(dates: RangePickerValue) {
		return dates?.some((date: dayjs.Dayjs | undefined, i) => {
			if (date && this.showTime) {
				const { disabledHours, disabledMinutes, disabledSeconds } = this.disabledTime(
					null,
					i ? 'end' : 'start'
				);
				return (
					disabledHours?.()?.includes(date.hour()) ||
					disabledMinutes?.()?.includes(date.minute()) ||
					disabledSeconds?.()?.includes(date.second())
				);
			}
			return false;
		});
	}

	private triggerValueChanged(
		dates: RangePickerValue,
		[isoStartDate, isoEndDate]: [string, string]
	) {
		if (this.__isSelectedTimeDisabled(dates)) {
			BCNotification.warning({
				autoClose: 3000,
				message: findMessage.get('102654'),
				description: findMessage
					.get('102655')
					.replace('$1', '')
					.replace('$2', findMessage.get('100348'))
			});

			const message: any = {
				typeName: 'ShowMessageAction',
				dialogType: 'Toaster',
				alertType: 'Warning',
				id: createGuid(),
				messages: findMessage
					.get('102655')
					.replace('$1', '')
					.replace('$2', findMessage.get('100348')),
				name: findMessage.get('102654'),
				enabled: true
			};
			Mobile.MobileView.mobileClientSideMessages([message]);
		} else {
			this.__proxyChanging.startDate.value = isoStartDate;
			this.__proxyChanging.endDate.value = isoEndDate;

			this.__proxyChanging.value = [isoStartDate, isoEndDate];
		}
	}

	focus() {
		this.inputRef.current?.focus();
	}

	blur() {
		this.inputRef.current?.blur();
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();

		return [
			...filterFields(fields, ['text', 'value', 'placeholder']),
			{
				name: 'format',
				type: 'string'
			},
			{
				name: 'showTime',
				type: 'boolean'
			},
			{
				name: 'showToday',
				type: 'boolean'
			},
			{
				name: 'mode',
				type: 'enum',
				enumMembers: ['time', 'date', 'month', 'year']
			},
			{
				name: 'disabledDateType',
				type: 'enum',
				enumMembers: ['past', 'before', 'after', 'dynamicRange']
			},
			{
				name: 'activeDateType',
				type: 'enum',
				enumMembers: ['range', 'month', 'week', 'year']
			},
			{
				name: 'startDate',
				type: 'object',
				items: [
					{
						name: 'allowEmpty',
						type: 'boolean',
						common: true
					},
					{
						name: 'setTodayAsDefault',
						type: 'boolean',
						common: true
					},
					{
						name: 'disabledHours',
						type: 'string'
					},
					{
						name: 'disabledMinutes',
						type: 'string'
					},
					{
						name: 'disabledSeconds',
						type: 'string'
					},
					{
						name: 'value',
						type: 'date',
						common: true
					}
				]
			},
			{
				name: 'endDate',
				type: 'object',
				items: [
					{
						name: 'allowEmpty',
						type: 'boolean',
						common: true
					},
					{
						name: 'setTodayAsDefault',
						type: 'boolean',
						common: true
					},
					{
						name: 'disabledHours',
						type: 'string'
					},
					{
						name: 'disabledMinutes',
						type: 'string'
					},
					{
						name: 'disabledSeconds',
						type: 'string'
					},
					{
						name: 'value',
						type: 'date',
						common: true
					}
				]
			}
		];
	}

	getPlaceholder(): [string, string] {
		return [
			geti18nValue(this.startDate.placeholder, this.__form.currentLanguage),
			geti18nValue(this.endDate.placeholder, this.__form.currentLanguage)
		];
	}

	getComponentValue(forMobile?: boolean): RangePickerValue {
		const format = this.format || dayjs().localeData().longDateFormat('L');
		if (forMobile) {
			return [
				this.startDate.value ? dayjs(new Date(this.startDate.value)).format(format) : null,
				this.endDate.value ? dayjs(new Date(this.endDate.value)).format(format) : null
			] as any;
		}
		return [
			this.startDate.value ? dayjs(new Date(this.startDate.value)) : undefined,
			this.endDate.value ? dayjs(new Date(this.endDate.value)) : undefined
		];
	}

	getRangeNumbers(str: string): Array<number> {
		const result: Array<number> = new Array();
		const ranges = str.split('-');
		if (ranges.length == 2) {
			for (let j = parseInt(ranges[0]); j <= parseInt(ranges[1]); j++) {
				result.push(j);
			}
		}
		return result;
	}

	getDisabledNumbers(str: string | Array<any>): Array<number> {
		if (str && typeof str === 'string') {
			const result: Array<number> = new Array();
			str = str.trim();
			const commas = str.split(',');
			if (commas.length > 0) {
				commas.forEach(i => {
					if (i.includes('-')) {
						result.push(...this.getRangeNumbers(i));
					} else {
						result.push(parseInt(i));
					}
				});
			} else if (str.includes('-')) {
				result.push(...this.getRangeNumbers(str));
			}
			return result;
		} else if (str && Array.isArray(str)) {
			return str.map(i => +i);
		}
		return [];
	}

	disabledTime(current: dayjs.Dayjs | undefined, type: 'start' | 'end') {
		return {
			disabledHours: () =>
				this.getDisabledNumbers((this as any)[type + 'Date'].disabledHours),
			disabledMinutes: () =>
				this.getDisabledNumbers((this as any)[type + 'Date'].disabledMinutes),
			disabledSeconds: () =>
				this.getDisabledNumbers((this as any)[type + 'Date'].disabledSeconds)
		};
	}

	disabledDate(current: dayjs.Dayjs): boolean {
		if (!current) {
			return;
		}

		if (this.dateLimitType === 'disabled') {
			switch (this.disabledDateType) {
				case 'past':
					return current < dayjs().startOf('day');
				case 'after':
					return this.disabledDates ? current > dayjs(this.disabledDates[0]) : false;
				case 'before':
					return this.disabledDates ? current < dayjs(this.disabledDates[0]) : false;
				case 'dynamicRange':
					return Math.abs(current.diff(dayjs(), 'hours')) > this.disabledDateRange * 24;
				default:
					return false;
			}
		}

		switch (this.activeDateType) {
			case 'week':
			case 'month':
			case 'year':
				const diffStart = dayjs().startOf(this.activeDateType);
				const diffEnd = dayjs().endOf(this.activeDateType);
				return current < diffStart || current > diffEnd;
			case 'range':
				const startDate = this.disabledDates?.[0]
					? dayjs(new Date(this.disabledDates[0])).startOf('day')
					: null;
				const endDate = this.disabledDates?.[1]
					? dayjs(new Date(this.disabledDates[1])).endOf('day')
					: null;
				return startDate && endDate ? current < startDate || current > endDate : false;
			default:
				return false;
		}
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			...(this.format ? {} : this.mode ? { mode: this.mode } : {}),
			format: this.format,
			showTime: this.showTime,
			disabledDateType: this.disabledDateType,
			activeDateType: this.activeDateType,
			showToday: this.showToday,
			style: { width: '100%', ...this.style },
			disabled: this.__isDisabled(),
			value: this.getComponentValue(true),
			allowClear: this.allowClear && !this.readOnly,
			disabledTime: this.disabledTime,
			allowEmpty: [this.startDate.allowEmpty, this.endDate.allowEmpty],
			disabledDate: this.disabledDate,
			size: this.sizeType,
			startDate: this.startDate,
			endDate: this.endDate,
			disabledDates: this.disabledDates,
			disabledDateRange: this.disabledDateRange
		};
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__renderPopupContainer() {
		if (!this.__form.getShadowDomMode()) {
			return (
				<Portal>
					<div id={pickerPopupId}></div>
				</Portal>
			);
		}
	}

	__getComponentByPlatform() {
		const format = this.format || dayjs().localeData().longDateFormat('L');

		if (mobile().any()) {
			return (
				<BCMobileRangePicker
					format={format}
					showTime={this.showTime}
					showToday={this.showToday}
					value={this.getComponentValue()}
					onChange={this.triggerValueChanged}
					readOnly={this.__isReadOnly()}
					startDate={this.startDate}
					endDate={this.endDate}
					disabledDateRange={this.disabledDateRange}
					disabledDateType={this.disabledDateType}
					activeDateType={this.activeDateType}
					disabledDates={this.disabledDates}
					dateLimitType={this.dateLimitType}
					allowClear={this.__isReadOnly() ? false : this.allowClear}
					disabled={this.__isDisabled()}
					placeholder={this.getPlaceholder()}
					mode={this.mode}
					textAlign={this.textAlign}
				/>
			);
		}

		return (
			<>
				{this.__renderPopupContainer()}
				<BCDateRangePicker
					ref={this.inputRef}
					tabIndex={this.tabIndex}
					{...(this.format ? {} : this.mode ? { mode: this.mode } : {})}
					format={format}
					showTime={this.showTime}
					showToday={this.showToday}
					style={{ width: '100%', ...this.style }}
					disabled={this.__isDisabled()}
					placeholder={this.getPlaceholder()}
					value={this.getComponentValue()}
					onChange={this.triggerValueChanged}
					allowClear={this.__isReadOnly() ? false : this.allowClear}
					disabledTime={this.disabledTime}
					allowEmpty={[this.startDate.allowEmpty, this.endDate.allowEmpty]}
					className={cx({
						transparentContent: true,
						fromMobile: this.__form.__fromMobile
					})}
					disabledDate={this.disabledDate}
					size={this.__getSizeType()}
					readOnly={this.__isReadOnly()}
					textAlign={this.textAlign}
					disabledDateType={this.disabledDateType}
					activeDateType={this.activeDateType}
					getPopupContainer={() => this.__form.__getShadowPopupContainer(pickerPopupId)}
					extractPopupKey={this.__form.getShadowDomMode() && pickerPopupId}
				/>
			</>
		);
	}

	__render(children?: React.ReactNode) {
		return super.__render(this.__getComponentByPlatform());
	}
}
